import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Box, ButtonBase } from "@mui/material"

import { dsColorNew } from "@palette"
import { USER_ROUTES } from "@constants"
import { AppCardTitle, AppInfoButton, AppNote, AppResultsSkeleton, AppStartHint } from "@components"
import IrisMark, { IRIS } from "@components/brand/IrisMark"
import { useToast } from "@hooks"
import { username } from "@signals/user"
import {
	irisProfile, irisProfileError, irisProfileOptions, irisProfileState, irisPrompt, irisSavedAt, loadIrisProfile, saveIrisProfile,
} from "@signals/irisProfile"
import { hasEditableProfile } from "@utils/irisProfile"
import { USER_SETTINGS_TABS_ID } from "../@constants"
import { SettingsStickyFooter } from "../@components"
import { identity, identityError, identityState, loadIdentity } from "../@signals"
import { changeCount, footerHint, fromServer, leaveIrisText, patchProfile, toPayload } from "./@services/profile"
import WhoYouAreSection from "./WhoYouAreSection"
import VoiceSection from "./VoiceSection"
import CompsetSection from "./CompsetSection"
import FocusSection from "./FocusSection"
import PromptSection from "./PromptSection"


// "Saved just now" for this long after a save (as Account)
const JUST_NOW_MS = 60e3

// Iris's mark on the navy tile inside the launcher's blue → cyan ring
const markTile = <Box component="span" sx={{
	width: 32, height: 32, borderRadius: '8px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
	border: '1.5px solid transparent',
	background: `linear-gradient(${dsColorNew.navyRaised}, ${dsColorNew.navyRaised}) padding-box, linear-gradient(135deg, ${dsColorNew.webBlue500}, ${dsColorNew.cyan}) border-box`,
}}><IrisMark size={IRIS.button}/></Box>

function IrisTitle({ dirty, subtitle }) {
	return <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
		<AppCardTitle title="Your Iris" subtitle={subtitle}/>
		<AppInfoButton title="Your profile, in three rules">
			<p><b>Preferences, not rules.</b> Iris uses this as her defaults: your compset, your target, your offices. The data and her own rules win over any preference — and she says so when they do.</p>
			<p><b>Yours, not your company's.</b> The profile belongs to your login. Two people of the same account each have their own.</p>
			<p><b>Nothing changes until you press Save.</b> No conversation with Iris writes here. What you read in <i>What Iris is told</i> is exactly what she receives.</p>
		</AppInfoButton>
		{dirty && <Box component="span" sx={{
			ml: 0.5, px: 1.25, py: 0.4, borderRadius: '999px', fontSize: 11, fontWeight: 600,
			backgroundColor: dsColorNew.noteAmberBg, color: dsColorNew.noteAmber, border: `1px solid ${dsColorNew.noteAmberBorder}`,
		}}>Unsaved changes</Box>}
	</Box>
}

const Title = (props) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>{markTile}<IrisTitle {...props}/></Box>

/**
 * User Settings › Iris (`/user/iris`, its own page since CU-869fmherf): the user's profile for Iris — a
 * draft over what the server saved, with Save / Discard.
 */
export default function UserSettingsIrisTab() {
	const { showToast } = useToast()
	const navigate = useNavigate()
	const [draft, setDraft] = useState(null)
	const [failed, setFailed] = useState(false)
	const [, tick] = useState(0)

	const state = irisProfileState.value
	const raw = irisProfile.value
	const saved = raw ? fromServer(raw) : null
	const value = draft ?? saved
	const changes = saved ? changeCount(saved, value) : 0
	const dirty = changes > 0
	const saving = state === 'saving'
	const savedAt = irisSavedAt.value
	const savedJustNow = !!savedAt && Date.now() - savedAt.getTime() < JUST_NOW_MS

	useEffect(() => {
		if (!savedJustNow) return
		const t = setTimeout(() => tick(n => n + 1), JUST_NOW_MS)
		return () => clearTimeout(t)
	}, [savedAt])

	const onChange = (next) => { setDraft(next); setFailed(false) }
	const onPatch = (next) => onChange(patchProfile(value, next))
	// Its links to Settings ask first when there are unsaved edits (CU-869fmherf D3). Only these: the side
	// menu, the launcher's cog and the header leave without asking, as everywhere else in the app (no
	// route guard with `BrowserRouter` 6.10)
	const leaveTo = (path) => {
		if (!dirty) return navigate(path)
		const { title, message } = leaveIrisText(changes)
		showToast.confirm(message, { title, tone: 'warning', submitText: 'Leave', cancelText: 'Stay', onSubmit: () => navigate(path) })
	}
	const openAccount = () => leaveTo(`${USER_ROUTES.SETTINGS_PATH}?tab=${USER_SETTINGS_TABS_ID.ACCOUNT}`)
	const openTiers = () => leaveTo(`${USER_ROUTES.SETTINGS_PATH}?tab=${USER_SETTINGS_TABS_ID.TIER}`)

	const onDiscard = () => showToast.confirm(
		`The ${changes} edit${changes > 1 ? 's' : ''} since your last save go away. Iris keeps the saved version.`,
		{ title: 'Discard your changes?', tone: 'danger', submitText: 'Discard', onSubmit: () => { setDraft(null); setFailed(false) } }
	)

	const onSave = async () => {
		const first = !hasEditableProfile(saved)
		const ok = await saveIrisProfile(toPayload(value))
		if (ok) {
			setDraft(null)
			setFailed(false)
			const welcome = first && hasEditableProfile(irisProfile.value) ? ' Iris greets you with her own welcome from your next visit.' : ''
			showToast.success(`Profile saved — what Iris is told has been updated.${welcome}`)
		} else {
			setFailed(true)
			showToast.error(`Could not save your profile · ${irisProfileError.value?.status || 'network error'}. Nothing was changed — your edits are still on screen.`)
		}
	}

	const subtitle = 'What she knows about you and how she talks to you. Everything here becomes part of her instructions — the exact text is at the bottom.'

	if (!saved && state !== 'error') return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} aria-busy="true">
		<Title subtitle="Loading your profile…"/>
		{[0, 1, 2, 3, 4].map(i => <AppResultsSkeleton key={i} rows={2} columns={3} height={120}/>)}
	</Box>

	if (!saved) {
		const e = irisProfileError.value
		return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			<Title subtitle={subtitle}/>
			{e?.status === 403
				? <AppNote severity="error"><b>Your Iris profile is not enabled for this account</b> (403). You can talk to Iris, but she cannot be told who you are yet. Ask Dataseekers support to enable the profile for <b>{username.value}</b>.</AppNote>
				: <AppNote severity="error">
					<b>Could not load your profile</b> — {e?.status || 'network error'}{e?.message ? ` ${e.message}` : ''}. Nothing was changed.{' '}
					<ButtonBase onClick={() => loadIrisProfile({ force: true })} sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: dsColorNew.appBlue, verticalAlign: 'baseline' }}>Try again</ButtonBase>
				</AppNote>}
			<Box sx={{ backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '14px' }}>
				<AppStartHint icon="eye" title="Nothing to edit until the profile loads" description="Iris keeps answering as she does today. Your saved profile, if any, is untouched." sx={{ py: 7 }}/>
			</Box>
		</Box>
	}

	const options = irisProfileOptions.value
	const axis = saved.compare?.axis ?? (identity.value?.kind === 'broker' ? 'site' : null)
	const hint = footerHint({ saved, dirty, changes, savedJustNow, error: failed ? irisProfileError.value : null })

	return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
		<Title dirty={dirty} subtitle={subtitle}/>
		<WhoYouAreSection
			identity={identity.value} state={identityState.value} error={identityError.value} compare={saved.compare}
			onOpenAccount={openAccount} onRetry={() => loadIdentity({ force: true })}
		/>
		<VoiceSection value={value} options={options} onPatch={onPatch} disabled={saving}/>
		<CompsetSection value={value} options={options} axis={axis} onPatch={onPatch} onChange={onChange} onOpenTiers={openTiers} disabled={saving}/>
		<FocusSection value={value} options={options} onPatch={onPatch} onChange={onChange} disabled={saving}/>
		<PromptSection prompt={irisPrompt.value} saved={saved} dirty={dirty}/>
		<SettingsStickyFooter
			variant="iris"
			hint={hint}
			onDiscard={onDiscard}
			discardDisabled={!dirty || saving}
			onSave={onSave}
			saveDisabled={!dirty || saving}
			saving={saving}
			saveLabel={saving ? 'Saving…' : failed && dirty ? 'Try again' : dirty ? 'Save changes' : 'Saved'}
		/>
	</Box>
}
